/**
 * JavaScript Lexer - Language Descriptor
 *
 * Configures the lexer framework for JavaScript tokenization.
 * Defines token types (whitespace, comments, strings, numbers,
 * identifiers, keywords, operators, punctuation) and matching rules.
 *
 * Whitespace and comments are flagged as IGNORE and excluded
 * from the lexer's tokenId count.
 */

import {
  Descriptor,
  Lexer,
  checkRegex,
  checkNull,
  createToken,
  type StateCheck,
} from './lexer'

const JS_KEYWORDS = new Set([
  'break',
  'case',
  'catch',
  'continue',
  'debugger',
  'default',
  'delete',
  'do',
  'else',
  'finally',
  'for',
  'function',
  'if',
  'in',
  'instanceof',
  'new',
  'return',
  'switch',
  'this',
  'throw',
  'try',
  'typeof',
  'var',
  'void',
  'while',
  'with',
  'class',
  'const',
  'enum',
  'export',
  'extends',
  'import',
  'super',
  'implements',
  'interface',
  'let',
  'package',
  'private',
  'protected',
  'public',
  'static',
  'yield',
  'async',
  'await',
  'of',
  'true',
  'false',
  'null',
  'undefined',
])

function createJSDescriptor(): Descriptor {
  const desc = new Descriptor(['IGNORE'], 'IGNORE')

  // Token types with their associated flags
  // Types with flag value 1 (IGNORE) won't be counted in lexer.tokenId
  desc.defineTypes({
    WHITESPACE: 1,
    LINE_COMMENT: 1,
    BLOCK_COMMENT: 1,
    STRING: 0,
    TEMPLATE: 0,
    NUMBER: 0,
    IDENTIFIER: 0,
    KEYWORD: 0,
    OPERATOR: 0,
    PUNCTUATION: 0,
  })

  desc.defineStateNames(['default'])

  const checks: StateCheck[] = [
    // Whitespace (ignored)
    [checkRegex('\\s+'), createToken(desc.WHITESPACE as number)],

    // Line comments (ignored)
    [checkRegex('\\/\\/[^\\n]*'), createToken(desc.LINE_COMMENT as number)],

    // Block comments (ignored)
    [
      checkRegex('\\/\\*[\\s\\S]*?\\*\\/'),
      createToken(desc.BLOCK_COMMENT as number),
    ],

    // Double-quoted strings
    [
      checkRegex('"(?:[^"\\\\\\n]|\\\\.)*"'),
      createToken(desc.STRING as number),
    ],

    // Single-quoted strings
    [
      checkRegex("'(?:[^'\\\\\\n]|\\\\.)*'"),
      createToken(desc.STRING as number),
    ],

    // Template strings (simplified - no nested expression handling)
    [checkRegex('`(?:[^`\\\\]|\\\\.)*`'), createToken(desc.TEMPLATE as number)],

    // Numbers: hex, binary, octal, decimal (with floats and exponents)
    [
      checkRegex(
        '(?:0[xX][0-9a-fA-F]+|0[bB][01]+|0[oO][0-7]+|(?:\\d+\\.?\\d*|\\.\\d+)(?:[eE][+-]?\\d+)?)',
      ),
      createToken(desc.NUMBER as number),
    ],

    // Identifiers and keywords
    [
      checkRegex('[a-zA-Z_$][a-zA-Z0-9_$]*'),
      function (this: Lexer, flags: number, p: number) {
        const text = this.text.substring(this.pos, p)
        const type = JS_KEYWORDS.has(text)
          ? (desc.KEYWORD as number)
          : (desc.IDENTIFIER as number)
        return this.createToken(type, flags, p)
      },
    ],

    // Multi-character operators (longest match first)
    [
      checkRegex(
        '>>>|===|!==|\\.\\.\\.|<<=|>>=|>>>=|\\*\\*=|&&=|\\|\\|=|\\?\\?=|' +
          '\\*\\*|==|!=|<=|>=|&&|\\|\\||\\?\\?|\\+\\+|--|\\+=|-=|\\*=|\\/=|%=|&=|\\|=|\\^=|<<|>>|=>|\\?\\.',
      ),
      createToken(desc.OPERATOR as number),
    ],

    // Default: any single character becomes punctuation
    [checkNull(), createToken(desc.PUNCTUATION as number)],
  ]

  desc.defineState(checks, 0)

  return desc
}

// Singleton descriptor (stateless, safe to reuse)
const jsDescriptor = createJSDescriptor()

/**
 * Create a new JavaScript lexer for the given source text.
 * Call getToken() repeatedly to iterate through tokens.
 */
export function createJSLexer(text: string): Lexer {
  return new Lexer(jsDescriptor, text)
}

export { jsDescriptor }
